import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { abrirCaixa, fecharCaixa, CaixaSession } from '@/integrations/supabase/caixa-sessions';
import { Loader2, CheckCircle2 } from 'lucide-react';
import { formatCurrency } from '@/utils/formatCurrency';

interface DialogAbrirFecharCaixaProps {
  isOpen: boolean;
  onClose: () => void;
  mode: 'abrir' | 'fechar';
  caixaAtual?: CaixaSession | null;
  onSuccess?: () => void;
}

export function DialogAbrirFecharCaixa({
  isOpen,
  onClose,
  mode,
  caixaAtual,
  onSuccess
}: DialogAbrirFecharCaixaProps) {
  const [saldo, setSaldo] = useState('');
  const [observacoes, setObservacoes] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async () => {
    if (!saldo || parseFloat(saldo) < 0) {
      toast.error('Saldo inválido');
      return;
    }

    setIsLoading(true);
    try {
      let result;

      if (mode === 'abrir') {
        result = await abrirCaixa(parseFloat(saldo), observacoes || undefined);
      } else {
        result = await fecharCaixa(parseFloat(saldo), observacoes || undefined);
      }

      if (result.ok) {
        toast.success(`Caixa ${mode === 'abrir' ? 'aberto' : 'fechado'} com sucesso!`);
        setSaldo('');
        setObservacoes('');
        onSuccess?.();
        onClose();
      } else {
        toast.error(result.error || 'Erro ao processar caixa');
      }
    } catch (erro: any) {
      toast.error(erro.message || 'Erro ao processar caixa');
    } finally {
      setIsLoading(false);
    }
  };

  const titulo = mode === 'abrir'
    ? '🔓 Abrir Caixa'
    : '🔒 Fechar Caixa';

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="border-purple-200">
        <DialogHeader>
          <DialogTitle className="text-purple-900 text-xl flex items-center gap-2">
            <CheckCircle2 className="w-5 h-5 text-purple-600" />
            {titulo}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {mode === 'fechar' && caixaAtual && (
            <div className="p-3 bg-purple-50 rounded-lg border border-purple-200">
              <p className="text-sm text-gray-600">Saldo Inicial</p>
              <p className="text-lg font-bold text-purple-900">
                {formatCurrency({ value: caixaAtual.saldo_inicial, currency: 'BRL' })}
              </p>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="saldo" className="text-purple-900 font-semibold">
              Saldo {mode === 'abrir' ? 'Inicial' : 'Final'} (R$)
            </Label>
            <Input
              id="saldo"
              type="number"
              step="0.01"
              min="0"
              placeholder="0,00"
              value={saldo}
              onChange={(e) => setSaldo(e.target.value)}
              className="border-purple-200 focus:border-purple-500 focus:ring-purple-500"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="obs" className="text-purple-900 font-semibold">
              Observações (opcional)
            </Label>
            <Textarea
              id="obs"
              placeholder={mode === 'abrir'
                ? 'Ex: Caixa com R$500 em dinheiro e R$200 em cartões'
                : 'Ex: Diferença de R$10 - verificar depois'}
              value={observacoes}
              onChange={(e) => setObservacoes(e.target.value)}
              rows={3}
              className="resize-none border-purple-200 focus:border-purple-500 focus:ring-purple-500"
            />
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} disabled={isLoading} className="border-purple-200">
            Cancelar
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isLoading || !saldo}
            className={mode === 'abrir'
              ? 'bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white'
              : 'bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-700 hover:to-red-700 text-white'
            }
          >
            {isLoading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Processando...
              </>
            ) : (
              <>
                <CheckCircle2 className="w-4 h-4 mr-2" />
                {mode === 'abrir' ? 'Abrir' : 'Fechar'} Caixa
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
